<template>
  <div :class="$style.base">
    <BaseHeading size="s">{{ $t('global.color') }}</BaseHeading>
    <Flex>
      <BaseColorLabel
        v-model="colorValue"
        colorPicker
        isSelected
        large
      />
      <div
        v-if="showAppointment"
        :class="$style.appointmentSection"
        v-test="'colorSelection-appointment'"
      >
        <div
          :class="$style.appointment"
          v-html="exampleAppointment"
        />
        <BaseText size="s">{{ $t('resource.appointment_look') }}</BaseText>
        <BaseText
          v-if="colorSettingChanged"
          @click="setColorSetting"
          v-test="'colorSelection-undo'"
        >
          {{ $t('global.undo') }}
        </BaseText>
      </div>
      <Flex
        v-else
        direction="column"
        :gap="0.5"
      >
        <BaseText>{{ $t(`${modelType}.calendar_color_warning`) }}</BaseText>
        <BaseText
          @click="setColorSetting"
          v-test="'colorSelection-settingButton'"
        >
          {{ $t(`${modelType}.calendar_color_change`) }}
        </BaseText>
      </Flex>
    </Flex>
  </div>
</template>

<script lang="ts">
import { mapState } from 'pinia';
import dayjs from '@/dayjs';
import createItem from '@/modules/calendar/calendar/calendar-content/CreateCalendarItem';
import { useCompanyStore } from '@/stores/company';
import { useResourcesStore } from '@/stores/resources';
import { useServicesStore } from '@/stores/services';

const colorSuggestions = [
  '#4061F2',
  '#03CFA5',
  '#EB4762',
  '#FD832D',
  '#DCED9B',
  '#E2CCF6',
  '#00C6D0',
  '#B7049E',
  '#2AB92C',
  '#A975E9',
  '#C28C27',
  '#F06494',
  '#60D180',
  '#EEA1A1',
  '#C2BF40',
  '#0995D2',
  '#B384C2',
  '#10B796',
  '#860606',
  '#CE47CE',
];

import { defineComponent } from 'vue';
import { useThemeStore } from '@/stores/theme';

export default defineComponent({
  props: {
    color: {
      type: String,
    },
    modelType: {
      type: String,
    },
    colorSetting: {
      type: String,
    },
  },
  emits: ['update:color', 'update:colorSetting'],
  data() {
    return {
      colorSettingChanged: false,
    };
  },
  computed: {
    ...mapState(useCompanyStore, ['companySettings']),
    ...mapState(useResourcesStore, ['allEmployees', 'allRoomsAndEquipment']),
    ...mapState(useServicesStore, {
      categories: 'serviceCategories',
    }),
    colorValue: {
      get() {
        return this.color;
      },
      set(value) {
        this.$emit('update:color', value);
      },
    },
    exampleAppointment() {
      const { darkerColors } = useThemeStore();
      const { companySettings } = useCompanyStore();
      const bufferTime =
        (companySettings.agenda?.appointmentEndBuffer &&
          companySettings.agenda?.appointmentEndBufferTime) ||
        0;

      const event = {
        title: this.$t('global.items.service', 1),
        startStr: dayjs(),
        endStr: dayjs()
          .add(1, 'hour')
          .add(bufferTime > 0 ? bufferTime : 0, 'minutes'),
        darkerColors,
        extendedProps: {
          type: 'appointment',
          color: this.color,
        },
        duration: 60 + (bufferTime > 0 ? bufferTime : 0),
      };
      return createItem({ event, bufferTime });
    },
    selectedSetting: {
      get() {
        return this.colorSetting || this.companySettings.agenda.colour;
      },
      set(value) {
        this.$emit('update:colorSetting', value);
      },
    },
    isCategory() {
      return this.modelType === 'category';
    },
    isEmployee() {
      return this.modelType === 'employee';
    },
    showAppointment() {
      return (
        (!this.isCategory && this.selectedSetting === 'RESOURCE') ||
        (this.isCategory && this.selectedSetting === 'CATEGORY')
      );
    },
    availableColors() {
      if (this.usedColors.length >= colorSuggestions.length) {
        return colorSuggestions;
      } else {
        return colorSuggestions.filter(
          (color) => !this.usedColors.includes(color),
        );
      }
    },
    randomColor() {
      return this.availableColors[
        Math.floor(Math.random() * this.availableColors.length)
      ];
    },
    usedColors() {
      return this.existingObjects
        .map((object) => object.color.toUpperCase())
        .filter((color) => colorSuggestions.includes(color));
    },
    existingObjects() {
      return this.isCategory ? this.categories : this.resources;
    },
    resources() {
      return this.isEmployee ? this.allEmployees : this.allRoomsAndEquipment;
    },
  },
  methods: {
    setColorSetting() {
      this.selectedSetting =
        this.selectedSetting === 'CATEGORY' ? 'RESOURCE' : 'CATEGORY';
      this.colorSettingChanged = !this.colorSettingChanged;
    },
  },
  created() {
    if (!this.color) {
      this.colorValue = this.randomColor;
    }
  },
});
</script>

<style
  lang="scss"
  module
>
.base {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: $spacing;
}

.appointmentSection {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: $spacing * 0.5;
}

.appointment {
  padding: $spacing * 0.5;
  border-radius: $radius;
  box-shadow: var(--shadow);
  width: 150px;
  margin: auto;

  & > * {
    pointer-events: none;
  }
}
</style>
